// iter541/iter566 — Extracted from BankStatementsPage.jsx
// Renders the "Auto-booked entries" history card with multi-select,
// per-row Undo, bulk-undo, and CSV export.

import { cur } from '@/lib/currency';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ChevronDown, ChevronRight, Sparkles, Trash2, Download } from 'lucide-react';

export function AutoBookedEntriesCard({
  entries,
  expanded,
  selected,
  onToggleExpand,
  onSelectAll,
  onSelectOne,
  onUndoOne,
  onBulkUndo,
  onExportCsv,
}) {
  if (!entries || entries.length === 0) return null;
  return (
    <Card className="border-border" data-testid="auto-booked-history-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-outfit text-base flex items-center justify-between gap-2 flex-wrap">
          <button
            type="button"
            onClick={onToggleExpand}
            className="flex items-center gap-2 text-left"
            data-testid="auto-booked-history-toggle"
          >
            {expanded ? <ChevronDown size={16} className="text-muted-foreground" /> : <ChevronRight size={16} className="text-muted-foreground" />}
            <Sparkles size={16} className="text-emerald-600" />
            Auto-booked entries
            <Badge variant="secondary" className="font-normal">{entries.length}</Badge>
          </button>
          <div className="flex items-center gap-2">
            {expanded && selected.size > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={onBulkUndo}
                className="h-8 text-xs border-rose-300 text-rose-700 hover:bg-rose-50 gap-1.5"
                data-testid="auto-booked-bulk-undo-btn"
              >
                <Trash2 size={13} /> Undo {selected.size}
              </Button>
            )}
            {expanded && (
              <Button
                variant="outline"
                size="sm"
                onClick={onExportCsv}
                className="h-8 text-xs gap-1.5"
                data-testid="auto-booked-export-csv-btn"
              >
                <Download size={13} /> Export CSV
              </Button>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      {expanded && (
        <CardContent className="pt-0">
          <div className="max-h-96 overflow-y-auto rounded-lg border border-border" data-testid="auto-booked-entries-list-table">
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead className="bg-muted/40 sticky top-0">
                  <tr className="text-left">
                    <th className="p-2 w-8">
                      <input
                        type="checkbox"
                        data-testid="auto-booked-pick-all"
                        checked={entries.length > 0 && selected.size === entries.length}
                        onChange={(e) => onSelectAll(e.target.checked)}
                      />
                    </th>
                    <th className="p-2">Booked on</th>
                    <th className="p-2">Type</th>
                    <th className="p-2">Category</th>
                    <th className="p-2">Reference</th>
                    <th className="p-2 text-right">Amount</th>
                    <th className="p-2">Expense</th>
                    <th className="p-2 w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e.marker_id} className="border-t border-border hover:bg-muted/30" data-testid={`auto-booked-row-${e.marker_id}`}>
                      <td className="p-2">
                        <input
                          type="checkbox"
                          checked={selected.has(e.marker_id)}
                          onChange={(ev) => onSelectOne(e.marker_id, ev.target.checked)}
                          data-testid={`auto-booked-pick-${e.marker_id}`}
                        />
                      </td>
                      <td className="p-2 whitespace-nowrap font-mono text-[10px]">{(e.booked_at || '').slice(0, 10)}</td>
                      <td className="p-2">
                        <Badge variant="outline" className={`text-[10px] ${e.marker_type === 'fee' ? 'border-sky-300 text-sky-800 bg-sky-50' : 'border-violet-300 text-violet-800 bg-violet-50'}`}>
                          {e.marker_type === 'fee' ? 'Fee' : 'Employee'}
                        </Badge>
                      </td>
                      <td className="p-2 font-medium">{e.label}</td>
                      <td className="p-2 max-w-[280px] truncate text-muted-foreground" title={e.expense_description || ''}>
                        {e.year_month
                          ? <span className="font-mono">{e.year_month}</span>
                          : (e.employee_name || e.expense_description?.slice(0, 60) || '—')}
                      </td>
                      <td className="p-2 text-right font-mono font-bold">{cur()} {Number(e.amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                      <td className="p-2 text-[10px] text-muted-foreground">
                        {e.expense_exists ? (
                          <span className="text-emerald-700 font-medium">✓ Active</span>
                        ) : (
                          <span className="text-rose-700">⚠ Deleted</span>
                        )}
                      </td>
                      <td className="p-2 text-right">
                        <button
                          type="button"
                          onClick={() => onUndoOne(e.marker_id)}
                          className="text-[11px] px-2 py-1 rounded-md border border-rose-300 text-rose-700 hover:bg-rose-50 transition-colors"
                          data-testid={`auto-booked-undo-${e.marker_id}`}
                        >
                          Undo
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
